/**
 * Canonical URL helpers for ordinary backend HTTP consumers.
 *
 * Campaign, character, session, invitation, lore, and admin requests share
 * exactly one `/api` prefix whether configuration provides an origin, a
 * prefixed base, or an accidentally duplicated prefix. Health, OAuth callback,
 * and WebSocket URLs retain their explicit builders and do not use this module.
 */
export function normalizeApiBaseUrl(value: string): string {
  const trimmedValue = value.trim();
  if (/[?#]/.test(trimmedValue)) {
    throw new Error('API base URL must not include a query string or hash fragment');
  }

  const withoutTrailingSlashes = trimmedValue.replace(/\/+$/, '');
  const origin = withoutTrailingSlashes.replace(/(?:\/api)+$/i, '');
  return `${origin}/api`;
}

/**
 * Append an ordinary API path to a canonical base.
 *
 * Accepting both prefixed and unprefixed paths lets active legacy callers
 * migrate without creating `/api/api` while later PRDs settle endpoint names.
 */
export function appendApiPath(apiBaseUrl: string, path: string): string {
  const base = normalizeApiBaseUrl(apiBaseUrl);
  const trimmedPath = path.trim();
  const suffixIndex = trimmedPath.search(/[?#]/);
  const pathname = suffixIndex === -1 ? trimmedPath : trimmedPath.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? '' : trimmedPath.slice(suffixIndex);
  let normalizedPath = pathname.replace(/^\/+/, '');

  while (normalizedPath.toLowerCase() === 'api' || /^api\//i.test(normalizedPath)) {
    normalizedPath = normalizedPath.toLowerCase() === 'api' ? '' : normalizedPath.slice(4);
  }

  return `${base}${normalizedPath ? `/${normalizedPath}` : ''}${suffix}`;
}
